import request from '@/utils/request';

type UploadSession = {
  upload_id: string;
  endpoint: string;
  bucket: string;
  object_key: string;
  access_key_id: string;
  access_key_secret: string;
  security_token: string;
  expiration: string;
  max_file_size: number;
};

export const uploadImage = async (blob: Blob, scene: 'dress_origin' | 'dress_thumb', fileName: string, itemId: string) => {
  // A retry of the same part reuses its receipt; editing its thumbnail changes the digest.
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer()));
  const requestId = itemId + '-' + Array.from(digest.slice(0, 16), byte => byte.toString(16).padStart(2, '0')).join('');
  const session = await request.post<never, UploadSession | { status: 'completed'; url: string }>('/api/upload/oss/create', {
    scene, file_name: fileName, file_size: blob.size, content_type: 'image/png',
    client_request_id: requestId,
  });
  if ('status' in session) {
    if (session.status === 'completed' && session.url) return session.url;
    throw new Error('未取得已审核的装扮图片');
  }
  const { endpoint, bucket, object_key, access_key_id, access_key_secret, security_token, expiration } = session;
  const host = new URL(endpoint);
  if (host.protocol !== 'https:' || !/^oss-[a-z0-9-]+\.aliyuncs\.com$/.test(host.host) ||
      !/^[a-z0-9-]+$/.test(bucket) || !object_key || !access_key_id || !access_key_secret || !security_token ||
      !(Date.parse(expiration) > Date.now()) || !(blob.size > 0 && blob.size <= session.max_file_size))
    throw new Error('图片上传凭证无效，请重试');
  // Same PostObject protocol as the H5 uploader; send only this object's STS credentials to OSS.
  const policy = btoa(JSON.stringify({ expiration, conditions: [
    { bucket }, { key: object_key }, { 'Content-Type': 'image/png' },
    { 'x-oss-security-token': security_token }, ['content-length-range', blob.size, blob.size],
  ] }));
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey('raw', encoder.encode(access_key_secret), { name: 'HMAC', hash: 'SHA-1' }, false, ['sign']);
  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(policy));
  const form = new FormData();
  Object.entries({ key: object_key, OSSAccessKeyId: access_key_id, policy,
    Signature: btoa(String.fromCharCode(...new Uint8Array(signature))), 'x-oss-security-token': security_token,
    'Content-Type': 'image/png', success_action_status: '204',
  }).forEach(([name, value]) => form.append(name, value));
  form.append('file', blob, fileName);
  const response = await fetch(`https://${bucket}.${host.host}/`, {
    method: 'POST', body: form, credentials: 'omit', referrerPolicy: 'no-referrer', redirect: 'error',
  });
  if (!response.ok) throw new Error('图片上传失败，请重试');
  const result = await request.post<never, { url?: string }>('/api/upload/oss/complete',
    { upload_id: session.upload_id }, { timeout: 60000 });
  if (!result.url) throw new Error('未取得已审核的装扮图片');
  return result.url;
};
